import React, { useMemo, useState } from 'react';
import { BookOpen, ChevronDown, ChevronRight, CheckCircle2, FileText, Loader2, Scale } from 'lucide-react';
import { ContractSection, ContractSubSection, RuleInfluence } from '../../services/Contract';
import { Badge } from '../ui/badge';
import { cn } from '@/lib/utils';
import { useI18n } from '../../i18n';

/**
 * Where the generated contract stands in project files:
 * - saving: export in progress
 * - saved: stored in a project (projectName shown when known)
 * - failed: export failed, can be retried
 * - unsaved: not saved yet (e.g. a chat from before auto-saving existed)
 */
export type ContractSaveStatus = 'saving' | 'saved' | 'failed' | 'unsaved';

interface ContractSectionsMessageProps {
  sections: ContractSection[];
  documentType: string;
  saveStatus?: ContractSaveStatus;
  projectName?: string | null;
  onOpenDocument?: () => void;
  onSave?: () => void;
  showAppliedRules?: boolean;
  activeRuleId?: string | null;
  onRuleHover?: (ruleId: string | null) => void;
  onRuleSelect?: (ruleId: string) => void;
}

const CONFIDENCE_STYLES = {
  high: 'bg-green-500 hover:bg-green-600',
  medium: 'bg-orange-500 hover:bg-orange-600',
  low: 'bg-gray-400 hover:bg-gray-500',
};

const collectRules = (
  items: (ContractSection | ContractSubSection)[],
  rules: Map<string, RuleInfluence>,
  quotedRuleIds: Set<string>,
) => {
  items.forEach((item) => {
    item.appliedRules?.forEach((rule) => {
      if (!rules.has(rule.ruleId)) rules.set(rule.ruleId, rule);
    });
    item.annotations?.forEach((annotation) => {
      if (annotation.ruleId) quotedRuleIds.add(annotation.ruleId);
    });
    if (item.subSections) collectRules(item.subSections, rules, quotedRuleIds);
  });
};

interface RuleChipProps {
  ruleId: string;
  label: string;
  active: boolean;
  onHover?: (ruleId: string | null) => void;
  onSelect?: (ruleId: string) => void;
  children: React.ReactNode;
}

const RuleChip: React.FC<RuleChipProps> = ({ ruleId, label, active, onHover, onSelect, children }) => {
  if (!onSelect) {
    return <span title={label}>{children}</span>;
  }
  return (
    <button
      type="button"
      title={label}
      aria-pressed={active}
      onClick={() => onSelect(ruleId)}
      onMouseEnter={() => onHover?.(ruleId)}
      onMouseLeave={() => onHover?.(null)}
      onFocus={() => onHover?.(ruleId)}
      onBlur={() => onHover?.(null)}
      className={cn('rounded-md focus:outline-none', active && 'ring-2 ring-brand ring-offset-1')}
    >
      {children}
    </button>
  );
};

export const ContractSectionsMessage: React.FC<ContractSectionsMessageProps> = ({
  sections,
  documentType,
  saveStatus,
  projectName,
  onOpenDocument,
  onSave,
  showAppliedRules = false,
  activeRuleId,
  onRuleHover,
  onRuleSelect,
}) => {
  const { t } = useI18n();
  const [showSections, setShowSections] = useState(false);
  const { allRules, quotedRuleIds } = useMemo(() => {
    const rules = new Map<string, RuleInfluence>();
    const quoted = new Set<string>();
    if (showAppliedRules) collectRules(sections, rules, quoted);
    return { allRules: Array.from(rules.values()), quotedRuleIds: quoted };
  }, [sections, showAppliedRules]);
  const chipProps = (rule: RuleInfluence, label: string) => {
    const selectable = !!onRuleSelect && quotedRuleIds.has(rule.ruleId);
    return {
      ruleId: rule.ruleId,
      label: selectable ? `${label} · ${t.qagentChat.contract.showInDocument}` : label,
      active: activeRuleId === rule.ruleId,
      onHover: selectable ? onRuleHover : undefined,
      onSelect: selectable ? onRuleSelect : undefined,
    };
  };

  let saveText: string | null = null;
  if (saveStatus === 'saving') {
    saveText = t.qagentChat.contract.savingToProject;
  } else if (saveStatus === 'saved') {
    saveText = projectName
      ? t.qagentChat.contract.savedToProject(projectName)
      : t.qagentChat.contract.savedToProjectFiles;
  } else if (saveStatus === 'failed') {
    saveText = t.qagentChat.contract.saveFailed;
  } else if (saveStatus === 'unsaved') {
    saveText = t.qagentChat.contract.notSaved;
  }

  return (
    <div className="rounded-xl border border-brand/20 bg-brand-light/30 overflow-hidden">
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-background border border-brand/30 text-brand flex-shrink-0">
          <CheckCircle2 className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {t.qagentChat.contract.ready(documentType)}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
            {saveStatus === 'saving' && <Loader2 className="h-3 w-3 animate-spin" />}
            <span>
              {t.qagentChat.contract.sectionsGenerated(sections.length)}
              {saveText && ` · ${saveText}`}
            </span>
          </p>
        </div>
        {saveStatus === 'saved' && onOpenDocument && (
          <button
            onClick={onOpenDocument}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-brand bg-background border border-brand/30 rounded-md hover:bg-brand-light transition-colors flex-shrink-0"
          >
            <FileText className="h-3.5 w-3.5" />
            {t.qagentChat.contract.openDocument}
          </button>
        )}
        {(saveStatus === 'failed' || saveStatus === 'unsaved') && onSave && (
          <button
            onClick={onSave}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-brand bg-background border border-brand/30 rounded-md hover:bg-brand-light transition-colors flex-shrink-0"
          >
            <FileText className="h-3.5 w-3.5" />
            {saveStatus === 'failed'
              ? t.qagentChat.contract.retrySave
              : t.qagentChat.contract.saveToProject}
          </button>
        )}
      </div>

      {allRules.length > 0 && (
        <div className="px-4 py-3 border-t border-brand/15 bg-background/40">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
            <BookOpen className="h-3.5 w-3.5 text-brand" />
            {t.qagentChat.contract.rulesApplied}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 mb-2">
            {t.qagentChat.contract.rulesAppliedDescription}
          </p>
          <div className="flex flex-wrap gap-1">
            {allRules.map((rule) => (
              <RuleChip key={rule.ruleId} {...chipProps(rule, rule.ruleName)}>
                <Badge variant="outline" className="text-[11px] font-medium hover:bg-brand-light/60">
                  {rule.ruleName}
                </Badge>
              </RuleChip>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={() => setShowSections((s) => !s)}
        className="w-full px-4 py-2 border-t border-brand/15 bg-background/50 hover:bg-background/80 flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        {showSections ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
        {showSections ? t.qagentChat.contract.hideSections : t.qagentChat.contract.showSections}
      </button>

      {showSections && (
        <div className="px-4 py-3 border-t border-brand/15 bg-background/40">
          <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
            {sections.map((section, idx) => {
              const sectionRules = showAppliedRules
                ? section.appliedRules?.filter((rule) => rule.confidence !== 'low') ?? []
                : [];
              return (
                <li key={idx} className="leading-relaxed">
                  {section.title}
                  {sectionRules.length > 0 && (
                    <div className="flex flex-wrap gap-1 pl-4 pt-1 pb-1">
                      {sectionRules.map((rule) => (
                        <RuleChip
                          key={rule.ruleId}
                          {...chipProps(rule, t.qagentChat.contract.ruleConfidence(rule.confidence ?? 'medium'))}
                        >
                          <Badge
                            className={cn(
                              'border-transparent text-white text-[10px] h-5 gap-1 px-1.5 font-medium shadow-none',
                              CONFIDENCE_STYLES[rule.confidence ?? 'medium'],
                            )}
                          >
                            <Scale className="h-3 w-3" />
                            {rule.ruleName}
                          </Badge>
                        </RuleChip>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
};
